/**
 * Validation utilities for hierarchical items
 * Checks required fields based on hierarchy type
 */

export interface ItemValidationResult {
  isValid: boolean
  missingFields: string[]
  warnings: string[]
  suggestions: string[]
}

export interface HierarchyItem {
  id?: string
  type: string
  details?: {
    clean_summary?: string
    action?: string
    description?: string
    timeline?: string
    [key: string]: any
  }
  classification?: {
    type?: string
    confidence?: number
  }
  status?: string
  [key: string]: any
}

/**
 * Get required fields for a hierarchy type
 */
export function getRequiredFields(hierarchyType: string): string[] {
  const typeMap: Record<string, string[]> = {
    'GOALS_OBJECTIVES': ['title'],
    'ASPIRATIONS_AMBITIONS': ['title'],
    'VISION_PURPOSE': ['title'],
    'TASKS_ACTIONS': ['title'],
    'ACTION_ITEMS': ['title'],
    'DREAMS': ['title'],
    'HABITS_ROUTINES': ['title'],
    'EVENTS_TIMEBOUND': ['title', 'timeline'],
  }
  
  return typeMap[hierarchyType] || ['title']
}

/**
 * Get recommended fields for a hierarchy type
 */
export function getRecommendedFields(hierarchyType: string): string[] {
  const typeMap: Record<string, string[]> = {
    'GOALS_OBJECTIVES': ['description', 'target_date', 'success_criteria'],
    'ASPIRATIONS_AMBITIONS': ['description', 'vision', 'target_date'],
    'VISION_PURPOSE': ['description', 'vision', 'motivation'],
    'TASKS_ACTIONS': ['description', 'due_date'],
    'ACTION_ITEMS': ['description', 'due_date'],
    'DREAMS': ['description', 'vision'],
    'HABITS_ROUTINES': ['description', 'frequency'],
    'EVENTS_TIMEBOUND': ['description', 'location'],
  }
  
  return typeMap[hierarchyType] || ['description']
}

/**
 * Get parent relationship suggestions based on hierarchy type
 */
export function getParentSuggestions(hierarchyType: string): string[] {
  const typeMap: Record<string, string[]> = {
    'GOALS_OBJECTIVES': ['parent_aspiration_id', 'parent_dream_id'],
    'ASPIRATIONS_AMBITIONS': ['parent_dream_id'],
    'TASKS_ACTIONS': ['parent_goal_id', 'parent_aspiration_id'],
    'ACTION_ITEMS': ['parent_task_id', 'parent_goal_id'],
  }
  
  return typeMap[hierarchyType] || []
}

/**
 * Validate a hierarchy item
 */
export function validateItem(item: HierarchyItem): ItemValidationResult {
  const hierarchyType = item.classification?.type || item.type || ''
  const requiredFields = getRequiredFields(hierarchyType)
  const recommendedFields = getRecommendedFields(hierarchyType)
  const parentSuggestions = getParentSuggestions(hierarchyType)
  
  const missingFields: string[] = []
  const warnings: string[] = []
  const suggestions: string[] = []
  
  // Check required fields
  for (const field of requiredFields) {
    if (field === 'title') {
      const title = item.details?.clean_summary || item.details?.action || ''
      if (!title || title.trim().length === 0) {
        missingFields.push('title')
      }
    } else if (field === 'timeline') {
      const timeline = item.details?.timeline
      if (!timeline || timeline.trim().length === 0) {
        missingFields.push('timeline')
      }
    }
  }
  
  // Check recommended fields
  for (const field of recommendedFields) {
    if (field === 'description') {
      const description = item.details?.description
      if (!description || description.trim().length === 0) {
        warnings.push('description')
        suggestions.push('Adding a description helps clarify what this item is about')
      }
    } else if (field === 'target_date' || field === 'due_date') {
      const date = item.details?.target_date || item.details?.due_date || item.details?.timeline
      if (!date) {
        warnings.push(field)
        suggestions.push(`Adding a ${field === 'target_date' ? 'target date' : 'due date'} helps with planning`)
      }
    }
  }
  
  // Check parent relationships (optional but recommended)
  if (parentSuggestions.length > 0) {
    const hasParent = parentSuggestions.some(field => {
      // Check if item has parent relationship info
      return item.details?.[field] || item[field]
    })
    
    if (!hasParent) {
      warnings.push('parent_relationship')
      const parentType = hierarchyType === 'GOALS_OBJECTIVES' 
        ? 'aspiration or dream'
        : hierarchyType === 'ASPIRATIONS_AMBITIONS'
        ? 'dream'
        : hierarchyType === 'TASKS_ACTIONS'
        ? 'goal or aspiration'
        : hierarchyType === 'ACTION_ITEMS'
        ? 'task or goal'
        : 'parent item'
      suggestions.push(`Consider linking this to a ${parentType} to show how it fits into your bigger picture`)
    }
  }
  
  return {
    isValid: missingFields.length === 0,
    missingFields,
    warnings,
    suggestions
  }
}

/**
 * Get user-friendly field names
 */
export function getFieldDisplayName(field: string): string {
  const displayNames: Record<string, string> = {
    'title': 'Title',
    'description': 'Description',
    'timeline': 'Timeline',
    'target_date': 'Target Date',
    'due_date': 'Due Date',
    'parent_aspiration_id': 'Linked Aspiration',
    'parent_dream_id': 'Linked Dream',
    'parent_goal_id': 'Linked Goal',
    'parent_task_id': 'Linked Task',
    'parent_relationship': 'Parent Relationship',
  }
  
  return displayNames[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

